import React, { useRef, useEffect, useCallback } from 'react';
import { Box } from '@chakra-ui/react';
import { useReducedMotion } from 'framer-motion';

// Color constants
const COLORS = {
  seed: '#6366F1',      // Indigo — AgentDeveloper
  mini: '#38BDF8',      // Cyan — Mini-agents
  micro: '#7DD3FC',     // Light cyan — Micro-agents
};

const MAX_NODES = 140;
const SPAWN_INTERVAL = 320;    // ms between spawns (accelerates)
const CONNECTION_DIST = 120;   // px — max distance to draw a line
const RESET_THRESHOLD = 135;   // reset when this many nodes reached

/**
 * Returns a random number in [min, max]
 */
const rand = (min, max) => Math.random() * (max - min) + min;

/**
 * Creates a node object
 * tier: 0 = seed, 1 = mini, 2 = micro
 */
function createNode(x, y, tier, parentId = null) {
  return {
    id: Math.random(),
    x,
    y,
    vx: rand(-0.3, 0.3),
    vy: rand(-0.3, 0.3),
    tier,
    parentId,
    radius: tier === 0 ? 10 : tier === 1 ? 6 : 3.5,
    pulse: rand(0, Math.PI * 2), // phase offset for pulsing
    spawnCount: 0,
    maxSpawns: tier === 0 ? 4 : tier === 1 ? 3 : 0,
    age: 0,
    opacity: 0, // fades in
    color: tier === 0 ? COLORS.seed : tier === 1 ? COLORS.mini : COLORS.micro,
  };
}

/**
 * Draws a single static snapshot of the network (reduced-motion fallback)
 */
function drawStaticNetwork(canvas, ctx) {
  const w = canvas.width;
  const h = canvas.height;
  ctx.clearRect(0, 0, w, h);

  // Create a representative static network
  const nodes = [];
  const cx = w * 0.45;
  const cy = h * 0.5;

  // Seed
  nodes.push({ x: cx, y: cy, tier: 0, radius: 10, color: COLORS.seed, opacity: 0.85 });

  // Mini ring
  for (let i = 0; i < 6; i++) {
    const angle = (i / 6) * Math.PI * 2;
    nodes.push({
      x: cx + Math.cos(angle) * 100,
      y: cy + Math.sin(angle) * 80,
      tier: 1, radius: 6, color: COLORS.mini, opacity: 0.7,
    });
  }

  // Micro ring
  for (let i = 0; i < 18; i++) {
    const angle = (i / 18) * Math.PI * 2 + 0.3;
    nodes.push({
      x: cx + Math.cos(angle) * rand(150, 220),
      y: cy + Math.sin(angle) * rand(120, 180),
      tier: 2, radius: 3.5, color: COLORS.micro, opacity: 0.5,
    });
  }

  // Draw lines
  for (let i = 0; i < nodes.length; i++) {
    for (let j = i + 1; j < nodes.length; j++) {
      const dx = nodes[i].x - nodes[j].x;
      const dy = nodes[i].y - nodes[j].y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < CONNECTION_DIST) {
        const alpha = (1 - dist / CONNECTION_DIST) * 0.25 * Math.min(nodes[i].opacity, nodes[j].opacity);
        ctx.beginPath();
        ctx.strokeStyle = `rgba(99,102,241,${alpha})`;
        ctx.lineWidth = 0.8;
        ctx.moveTo(nodes[i].x, nodes[i].y);
        ctx.lineTo(nodes[j].x, nodes[j].y);
        ctx.stroke();
      }
    }
  }

  // Draw nodes
  nodes.forEach((n) => {
    // Glow
    const gradient = ctx.createRadialGradient(n.x, n.y, 0, n.x, n.y, n.radius * 3);
    gradient.addColorStop(0, n.color + 'aa');
    gradient.addColorStop(1, n.color + '00');
    ctx.beginPath();
    ctx.arc(n.x, n.y, n.radius * 2.5, 0, Math.PI * 2);
    ctx.fillStyle = gradient;
    ctx.fill();

    // Core
    ctx.beginPath();
    ctx.arc(n.x, n.y, n.radius, 0, Math.PI * 2);
    ctx.fillStyle = n.color;
    ctx.globalAlpha = n.opacity;
    ctx.fill();
    ctx.globalAlpha = 1;
  });
}

export default function LivingNetworkCanvas() {
  const canvasRef = useRef(null);
  const animFrameRef = useRef(null);
  const stateRef = useRef({
    nodes: [],
    spawnTimer: 0,
    spawnInterval: SPAWN_INTERVAL,
    lastTime: 0,
    running: false,
  });
  const prefersReduced = useReducedMotion();

  // Resize handler — updates canvas resolution
  const handleResize = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const parent = canvas.parentElement;
    if (!parent) return;
    const rect = parent.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = rect.height;
  }, []);

  // Spawns a child node near a parent
  const spawnChild = useCallback((parent, canvasW, canvasH) => {
    if (parent.spawnCount >= parent.maxSpawns) return null;
    const angle = rand(0, Math.PI * 2);
    const dist = rand(60, 130);
    const nx = Math.max(20, Math.min(canvasW - 20, parent.x + Math.cos(angle) * dist));
    const ny = Math.max(20, Math.min(canvasH - 20, parent.y + Math.sin(angle) * dist));
    const childTier = parent.tier === 0 ? 1 : 2;
    const child = createNode(nx, ny, childTier, parent.id);
    parent.spawnCount += 1;
    return child;
  }, []);

  // Main animation loop
  const animate = useCallback((timestamp) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const state = stateRef.current;
    const dt = Math.min(timestamp - (state.lastTime || timestamp), 50); // cap delta
    state.lastTime = timestamp;

    const w = canvas.width;
    const h = canvas.height;

    // Clear
    ctx.clearRect(0, 0, w, h);

    // --- Spawn logic ---
    state.spawnTimer += dt;
    if (state.spawnTimer >= state.spawnInterval && state.nodes.length < MAX_NODES) {
      state.spawnTimer = 0;

      // Find eligible parents
      const eligibleParents = state.nodes.filter(
        (n) => n.spawnCount < n.maxSpawns && n.age > 800 && n.tier < 2
      );

      if (eligibleParents.length > 0) {
        // More nodes = faster spawning (exponential feel)
        const speedFactor = Math.max(0.3, 1 - state.nodes.length / MAX_NODES);
        state.spawnInterval = SPAWN_INTERVAL * speedFactor;

        // Spawn 1–3 nodes per tick depending on how many parents are available
        const batchSize = Math.min(Math.ceil(eligibleParents.length / 3), 4);
        for (let b = 0; b < batchSize; b++) {
          const parent = eligibleParents[Math.floor(Math.random() * eligibleParents.length)];
          const child = spawnChild(parent, w, h);
          if (child) state.nodes.push(child);
        }
      }

      // Reset cycle
      if (state.nodes.length >= RESET_THRESHOLD) {
        setTimeout(() => {
          if (!stateRef.current.running) return;
          stateRef.current.nodes = [createNode(w * 0.45, h * 0.5, 0)];
          stateRef.current.spawnInterval = SPAWN_INTERVAL;
        }, 2000);
      }
    }

    // --- Update nodes ---
    state.nodes.forEach((n) => {
      n.age += dt;
      // Fade in
      if (n.opacity < 1) n.opacity = Math.min(1, n.opacity + dt / 600);

      // Drift
      n.x += n.vx;
      n.y += n.vy;

      // Gentle drift correction (keep on canvas)
      if (n.x < 20 || n.x > w - 20) n.vx *= -1;
      if (n.y < 20 || n.y > h - 20) n.vy *= -1;

      // Slight random walk
      n.vx += rand(-0.02, 0.02);
      n.vy += rand(-0.02, 0.02);
      // Dampen velocity
      n.vx *= 0.99;
      n.vy *= 0.99;
      n.vx = Math.max(-0.6, Math.min(0.6, n.vx));
      n.vy = Math.max(-0.6, Math.min(0.6, n.vy));

      // Pulse phase
      n.pulse += dt * 0.002;
    });

    // --- Draw connections ---
    for (let i = 0; i < state.nodes.length; i++) {
      for (let j = i + 1; j < state.nodes.length; j++) {
        const a = state.nodes[i];
        const b = state.nodes[j];
        const dx = a.x - b.x;
        const dy = a.y - b.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < CONNECTION_DIST) {
          const alpha =
            (1 - dist / CONNECTION_DIST) *
            0.3 *
            Math.min(a.opacity, b.opacity);
          ctx.beginPath();
          const lineColor =
            a.tier === 0 || b.tier === 0
              ? `rgba(99,102,241,${alpha})`
              : `rgba(56,189,248,${alpha * 0.7})`;
          ctx.strokeStyle = lineColor;
          ctx.lineWidth = a.tier === 0 || b.tier === 0 ? 1 : 0.6;
          ctx.moveTo(a.x, a.y);
          ctx.lineTo(b.x, b.y);
          ctx.stroke();
        }
      }
    }

    // --- Draw nodes ---
    state.nodes.forEach((n) => {
      const pulse = 0.85 + Math.sin(n.pulse) * 0.15;
      const displayRadius = n.radius * pulse;

      ctx.globalAlpha = n.opacity * 0.7;

      // Glow halo
      const glowRadius = displayRadius * 3.5;
      const gradient = ctx.createRadialGradient(n.x, n.y, 0, n.x, n.y, glowRadius);
      gradient.addColorStop(0, n.color + '55');
      gradient.addColorStop(1, n.color + '00');
      ctx.beginPath();
      ctx.arc(n.x, n.y, glowRadius, 0, Math.PI * 2);
      ctx.fillStyle = gradient;
      ctx.fill();

      // Core circle
      ctx.globalAlpha = n.opacity;
      ctx.beginPath();
      ctx.arc(n.x, n.y, displayRadius, 0, Math.PI * 2);
      ctx.fillStyle = n.color;
      ctx.fill();

      ctx.globalAlpha = 1;
    });

    animFrameRef.current = requestAnimationFrame(animate);
  }, [spawnChild]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    handleResize();

    if (prefersReduced) {
      // Static snapshot
      const ctx = canvas.getContext('2d');
      requestAnimationFrame(() => drawStaticNetwork(canvas, ctx));
      return;
    }

    // Initialize seed node
    const w = canvas.width;
    const h = canvas.height;
    stateRef.current.nodes = [createNode(w * 0.45, h * 0.5, 0)];
    stateRef.current.running = true;
    animFrameRef.current = requestAnimationFrame(animate);

    const parent = canvas.parentElement;
    if (!parent) return;

    const resizeObserver = new ResizeObserver(() => {
      handleResize();
      const nw = canvas.width;
      const nh = canvas.height;
      stateRef.current.nodes = [createNode(nw * 0.45, nh * 0.5, 0)];
      stateRef.current.spawnInterval = SPAWN_INTERVAL;
    });
    resizeObserver.observe(parent);

    return () => {
      stateRef.current.running = false;
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      resizeObserver.disconnect();
    };
  }, [prefersReduced, animate, handleResize]);

  return (
    <Box
      position="absolute"
      inset={0}
      aria-hidden="true"
      role="presentation"
    >
      <canvas
        ref={canvasRef}
        style={{
          display: 'block',
          width: '100%',
          height: '100%',
          opacity: 0.7,
        }}
      />
    </Box>
  );
}